import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import Footer from '../components/Footer';

const TermsPage = () => {
  return (
    <div className="privacy-page min-h-screen" data-testid="terms-page">
      <Helmet>
        <title>Умови користування — CIMM Computers Одеса</title>
        <meta name="description" content="Умови надання послуг сервісного центру CIMM Computers у місті Одеса. ФОП Войчук Дмитро Володимирович." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://cimm.od.ua/terms" />
      </Helmet>

      <header className="cyber-header fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="text-2xl font-bold neon-text">CIMM</div>
            <div className="text-sm text-gray-400 hidden md:block">Комп'ютери та Ремонт</div>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-gray-300 hover:text-orange-400/80 transition-all">Головна</Link>
            <Link to="/phones" className="text-gray-300 hover:text-orange-400/80 transition-all">Телефони</Link>
            <Link to="/laptops" className="text-gray-300 hover:text-orange-400/80 transition-all">Ноутбуки</Link>
            <Link to="/contacts" className="text-gray-300 hover:text-orange-400/80 transition-all">Контакти</Link>
          </nav>
          <a href="tel:+380974178788" className="neon-text font-semibold">+38 097 417 8788</a>
        </div>
      </header>

      <section className="pt-32 pb-16 px-4 dark-section">
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-orange-500 icon-glow" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Умови <span className="neon-text">користування</span>
            </h1>
            <p className="text-gray-400">Дата набуття чинності: 1 січня 2026 року</p>
          </div>

          <div className="glass-card p-8 space-y-8 text-gray-300 leading-relaxed" data-testid="terms-content">
            <div>
              <h2 className="text-2xl font-bold text-white mb-3">1. Загальні положення</h2>
              <p>
                Ці Умови користування регулюють відносини між користувачами сайту <strong>cimm.od.ua</strong> (далі — «Сайт»)
                та ФОП Войчук Дмитро Володимирович (далі — «Виконавець», «ми»), який надає послуги з ремонту та
                обслуговування електронної техніки в місті Одеса.
              </p>
              <p className="mt-2">
                Використовуючи Сайт, ви (далі — «Замовник», «Клієнт», «Користувач») підтверджуєте, що ознайомлені та
                погоджуєтесь з цими Умовами у повному обсязі.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-3">2. Послуги</h2>
              <p className="mb-2">Виконавець надає наступні послуги:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Діагностика та ремонт ноутбуків, комп'ютерів, MacBook;</li>
                <li>Ремонт мобільних телефонів (заміна дисплея, акумулятора, компонентів);</li>
                <li>Ремонт та обслуговування ігрових приставок PlayStation 4/5, Xbox;</li>
                <li>Чистка від пилу, заміна термопасти та термопрокладок;</li>
                <li>Відновлення даних, встановлення програмного забезпечення;</li>
                <li>Інші послуги, погоджені з Клієнтом у індивідуальному порядку.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-3">3. Замовлення послуг</h2>
              <p>
                Замовлення оформлюється через форму на сайті, за телефоном або особисто в сервісному центрі.
                Первинна діагностика для більшості типів техніки — <strong>безкоштовна</strong>. Вартість робіт
                погоджується з Клієнтом до початку ремонту. Ремонт розпочинається виключно після усного або
                письмового погодження вартості та строків.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-3">4. Вартість та оплата</h2>
              <p>
                Вартість послуг вказана у гривнях (UAH) і може змінюватися залежно від складності робіт, наявності
                запчастин та інших факторів. Ціни на Сайті носять інформаційний характер і не є публічною офертою.
                Остаточна вартість фіксується після діагностики. Оплата здійснюється готівкою або безготівковим
                переказом після виконання робіт.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-3">5. Гарантія</h2>
              <p>
                На виконані роботи надається гарантія від <strong>14 до 90 днів</strong> залежно від типу послуги.
                Термін гарантії вказується у квитанції. Гарантія не поширюється на випадки механічних пошкоджень,
                потрапляння вологи, сторонніх втручань після ремонту, а також на витратні матеріали.
                Деталі — на сторінці <Link to="/garantiya" className="text-orange-400 hover:underline">Гарантія та повернення</Link>.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-3">6. Відповідальність</h2>
              <p>
                Виконавець відповідає за якість виконаних робіт у межах вартості наданої послуги. Виконавець не несе
                відповідальності за втрату даних, що зберігалися на пристрої. Клієнт зобов'язаний завчасно виконати
                резервне копіювання важливих даних.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-3">7. Права інтелектуальної власності</h2>
              <p>
                Весь контент Сайту (тексти, зображення, логотипи, дизайн) є власністю CIMM Computers або
                використовується на законних підставах. Копіювання матеріалів без письмового дозволу заборонено.
                Назви брендів (Apple, Samsung, Sony, Microsoft тощо) належать їхнім власникам і використовуються
                виключно для ідентифікації послуг.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-3">8. Обробка персональних даних</h2>
              <p>
                Порядок збору, зберігання та обробки персональних даних описано у{' '}
                <Link to="/privacy-policy" className="text-orange-400 hover:underline">Політиці конфіденційності</Link>.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-3">9. Зміни до умов</h2>
              <p>
                Виконавець залишає за собою право у будь-який час вносити зміни до цих Умов. Оновлена версія
                публікується на Сайті із зазначенням дати. Продовжуючи користуватися Сайтом після внесення змін,
                Користувач погоджується з новою редакцією.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-3">10. Реквізити</h2>
              <p>
                <strong>ФОП Войчук Дмитро Володимирович</strong><br />
                Місто Одеса, Україна<br />
                Телефон: <a href="tel:+380974178788" className="text-orange-400 hover:underline">+38 097 417 8788</a><br />
                Email: <a href="mailto:info@cimm.od.ua" className="text-orange-400 hover:underline">info@cimm.od.ua</a><br />
                Сайт: <a href="https://cimm.od.ua" className="text-orange-400 hover:underline">cimm.od.ua</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage;
